import { Box, Grid, Stack, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import ChangePasswordDialog from "../../../components/Dialog/ChangePasswordDialog";
import ProfileSaveDialog from "../../../components/Dialog/ProfileSaveDialog";
import UploadImageAvatar from "../../../components/UploadImage";
import globalStyle from "../../../globalStyle";
import { setUserData } from "../../../lib/slices/userSlice";
import {
  changePasswordAdmin,
  updateProfileAdmin,
} from "../../../services/authenticationApiFn";
import { encryptPassword, handleOpenSnackBar } from "../../../utils";
import Label from "./../../../components/Label/index";
import TextInput from "./../../../components/TextInputs/index";
import { baseUrl } from "../../../services";
import { useTranslation } from "react-i18next";

function Profile() {
  const { t } = useTranslation(["setting", "common"]);
  // changepassword dialog
  const openCpDialog = useRef(null);
  // profile save dialog
  const opensPsDialog = useRef(null);

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [inputValues, setInputValues] = useState({
    image: "",
    name: "",
    email: "",
    contact: "",
    facebookLink: "",
    instagramLink: "",
    youtubeLink: "",
    linkedInLink: "",
  });

  const [inputError, setInputError] = useState({
    image: false,
    name: false,
    email: false,
    isEmailValid: false,
    contact: false,
  });

  const userData = useSelector((state) => state.userData.user);

  useEffect(() => {
    if (userData) {
      setInputValues((prev) => ({
        ...prev,
        image: userData.profileImg,
        name: userData.name,
        email: userData.email,
        contact: userData.contact,
        facebookLink: userData?.facebookLink,
        instagramLink: userData?.instagramLink,
        youtubeLink: userData?.youtubeLink,
        linkedInLink: userData?.linkedInLink,
      }));
    }
  }, [userData]);

  const handleChangeInputValues = (event) => {
    const { name, value } = event.target;

    setInputError((prevError) => ({
      ...prevError,
      [name]: false,
      isEmailValid: name === "email" ? false : inputError.isEmailValid,
    }));
    setInputValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = async (event) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInputValues((prevValues) => ({
        ...prevValues,
        image: response?.data?.url,
      }));
      setInputError((prevError) => ({
        ...prevError,
        image: false,
      }));
      handleOpenSnackBar("success", t("imageUploadSuccess"));
    } catch (error) {
      handleOpenSnackBar("error", t("imageUploadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={2}
        direction={"row"}
        alignItems={"center"}
        flexGrow={1}
        justifyContent={"flex-end"}
      >
        <Box height={40}>
          <GlobalButton
            variant={"outlined"}
            btnText={"resetPassword"}
            styleProps={{ width: "auto" }}
            handleClickBtn={() => openCpDialog.current.openDialog()}
          />
        </Box>
        <Box width={100} height={40}>
          <GlobalButton
            variant={"contained"}
            btnText={"save"}
            styleProps={{ fontWeight: 400 }}
            handleClickBtn={() => opensPsDialog.current.openDialog()}
          />
        </Box>
      </Stack>
    );
  };

  const handleChangePassword = async (data) => {
    try {
      const payload = {
        userId: userData._id,
        newPassword: encryptPassword(data.newPassword),
        oldPassword: encryptPassword(data.oldPassword),
        role: "Admin",
      };
      setIsLoading(true);
      const response = await changePasswordAdmin(payload);
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        openCpDialog.current.closeDialog();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProfile = async (password) => {
    let payload = {
      name: inputValues.name,
      email: inputValues.email,
      contact: inputValues.contact,
      profileImg: inputValues.image,
      facebookLink: inputValues.facebookLink,
      instagramLink: inputValues.instagramLink,
      youtubeLink: inputValues.youtubeLink,
      linkedInLink: inputValues.linkedInLink,
      password: encryptPassword(password),
    };

    try {
      setIsLoading(true);
      const response = await updateProfileAdmin(userData?._id, payload);
      if (response?.data.status === "success") {
        const data = response?.data?.data;
        const updatedData = { ...userData, ...data };
        localStorage.setItem("user_data_admin", JSON.stringify(updatedData));
        dispatch(setUserData(updatedData));
        handleOpenSnackBar("success", response.data.message);
        opensPsDialog.current.closeDialog();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("personalDetails")}
        subtitle={t("managePersonalDetails")}
        headerActions={renderHeaderAction()}
      >
        <Typography
          variant="h1"
          fontWeight={600}
          fontSize={"1.125rem"}
          mb={3}
          textTransform={"capitalize"}
        >
          {t("personalInformation")}
        </Typography>

        <Grid container mt={5}>
          <Grid item xs={12} md={3}>
            <UploadImageAvatar
              error={false}
              image={inputValues.image}
              handleImageChange={handleImageChange}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Label labelText={t("name")} required htmlFor={"admin-name"} />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"admin-name"}
                  required
                  inputError={inputError.name}
                  helperText={inputError.name && t("userNameRequired")}
                  placeholder={t("enterName")}
                  name={"name"}
                  value={inputValues.name}
                  onChange={handleChangeInputValues}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Label labelText={t("email")} required htmlFor={"email"} />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"email"}
                  placeholder={t("enterEmail")}
                  inputError={inputError.email || inputError.isEmailValid}
                  helperText={
                    inputError.email
                      ? t("emailRequired")
                      : inputError.isEmailValid
                        ? t("invalidEmailFormat")
                        : null
                  }
                  required
                  readonly
                  name={"email"}
                  value={inputValues.email}
                  onChange={handleChangeInputValues}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Label
                  labelText={t("contact")}
                  required
                  htmlFor={"business-contact"}
                />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"business-contact"}
                  placeholder={t("enterContact")}
                  inputError={inputError.contact}
                  helperText={inputError.contact && t("contactNumberRequired")}
                  required
                  name={"contact"}
                  value={inputValues.contact}
                  onChange={handleChangeInputValues}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container mt={3} spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h1"
              fontWeight={600}
              fontSize={"1.125rem"}
              mb={1}
            >
              {t("facebookPageLink")}
            </Typography>
            <TextInput
              placeholder={t("enterFacebookPageLink")}
              id={"sp-fb-link"}
              value={inputValues.facebookLink}
              name={"facebookLink"}
              onChange={handleChangeInputValues}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h1"
              fontWeight={600}
              fontSize={"1.125rem"}
              mb={1}
            >
              {t("instagramLink")}
            </Typography>
            <TextInput
              placeholder={t("enterInstagramLink")}
              id={"sp-ig-link"}
              value={inputValues.instagramLink}
              name={"instagramLink"}
              onChange={handleChangeInputValues}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h1"
              fontWeight={600}
              fontSize={"1.125rem"}
              mb={1}
            >
              {t("youtubeLink")}
            </Typography>
            <TextInput
              placeholder={t("enterYoutubeLink")}
              id={"sp-yt-link"}
              value={inputValues.youtubeLink}
              name={"youtubeLink"}
              onChange={handleChangeInputValues}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h1"
              fontWeight={600}
              fontSize={"1.125rem"}
              mb={1}
            >
              {t("linkedinLink")}
            </Typography>
            <TextInput
              placeholder={t("enterLinkedInLink")}
              id={"sp-lk-link"}
              value={inputValues.linkedInLink}
              name={"linkedInLink"}
              onChange={handleChangeInputValues}
            />
          </Grid>
        </Grid>
      </PaperContainer>
      <ChangePasswordDialog
        ref={openCpDialog}
        handleChangePassword={handleChangePassword}
      />
      <ProfileSaveDialog
        ref={opensPsDialog}
        handleSaveProfile={handleSaveProfile}
      />
    </>
  );
}

export default Profile;
